/**
 * pdfTables.js — Tableaux de données du rapport PDF (stats PM + résumé).
 *
 * Extrait de pdfHelpers.js (refacto structurelle 1.4b-0, cap 500 lignes).
 * Les primitives partagées (BRAND, checkPageBreak) restent dans ./pdfHelpers.
 *
 * 10.5.315 (D241) : le tableau montre le temps au-dessus du seuil
 * d'avertissement (palier en mots, tri-couleur) puis du seuil d'alerte, en
 * part et en durée, lus dans l'objet d'exposition des cartes de l'écran.
 */
import i18n from '../i18n';
import { classifyPm } from '../constants/classifyPm';
import { classifyTimeAbove, TIME_ABOVE_PCT } from '../constants/classifyTimeAbove';
import { BRAND, checkPageBreak } from './pdfHelpers';
import { formatNumberLocale } from '../utils/formatters';
import { buildPeriodAnalysis } from '../sessions/period/periodAnalysis';
import { formatMinutesAbove } from '../sessions/period/periodText';

// « Non mesuré » en petit corps : tient dans les colonnes de 18 mm (Min, Moy…).
const NOT_MEASURED_FONT = 6.5;
const VALUE_FONT = 8;
const DURATION_FONT = 7;
const LEGEND_FONT = 7;

// Hauteurs de ligne (mm) : les cellules de temps au-dessus d'un seuil tiennent
// sur deux lignes (part puis durée), d'où des lignes PM plus hautes.
const ROW_H = 9;
const PM_ROW_H = 12;
const LINE_GAP = 4.5;

/**
 * Colonnes du tableau, en part de la largeur utile (A4 portrait, 180 mm) :
 * Paramètre | Min | Moy | Max | P95 | Temps > avert. | Temps > alerte | Évaluation.
 * Largeurs vérifiées sur le vrai jsPDF (pdfStatsTable.layout.test) : le texte
 * le plus long de chaque colonne, FR et EN, y tient sans chevauchement.
 */
export const STATS_COLUMNS = Object.freeze([0.15, 0.10, 0.10, 0.10, 0.10, 0.17, 0.13, 0.15]);
export const CELL_PADDING = 2;

// Mention en petit corps gris dans une cellule (« Non mesuré », « Non affiché »).
function drawMuted(pdf, text, x, y) {
  pdf.setFontSize(NOT_MEASURED_FONT);
  pdf.setTextColor(...BRAND.gray);
  pdf.text(text, x, y);
  pdf.setFontSize(VALUE_FONT);
  pdf.setTextColor(...BRAND.dark);
}

// Valeur d'une cellule numérique ; absente : « Non mesuré » en gris.
function drawValue(pdf, value, x, y, suffix = '') {
  if (value == null) {
    drawMuted(pdf, i18n.t('common.notMeasured'), x, y);
    return;
  }
  pdf.text(`${formatNumberLocale(value, 1)}${suffix}`, x, y);
}

// Présentation PDF des 3 paliers classifyPm (source unique écran/backend, D169).
const PM_EVAL = {
  bon:          { labelKey: 'alerts.level.good',  color: BRAND.green },
  a_surveiller: { labelKey: 'alerts.level.watch', color: BRAND.orange },
  eleve:        { labelKey: 'alerts.level.high',  color: BRAND.red },
  non_mesure:   { labelKey: 'common.notMeasured', color: BRAND.gray },
};

// Tri-couleur D3 du temps au-dessus du seuil d'avertissement (classifyTimeAbove,
// mêmes bornes que la ligne des cartes de l'écran).
const TIME_ABOVE_COLOR = { low: BRAND.green, mid: BRAND.orange, high: BRAND.red };

/**
 * Textes d'une cellule « temps au-dessus d'un seuil » : ceux des cartes de
 * l'écran (même part, même durée formatMinutesAbove, même palier en mots).
 * `withLevel` : seuil d'avertissement, palier en mots et couleur ; le seuil
 * d'alerte reste neutre, comme dans « Détails ». null si rien n'est mesuré.
 */
export function timeAboveCell(above, withLevel) {
  if (above?.pct == null) return null;
  const pct = i18n.t('pdf.stats.pct', { pct: formatNumberLocale(above.pct, 1) });
  const level = classifyTimeAbove(above.pct);
  return {
    main: withLevel ? `${pct} · ${i18n.t(`session.stats.timeAboveLevel.${level}`)}` : pct,
    duration: formatMinutesAbove(above.minutes),
    color: withLevel ? TIME_ABOVE_COLOR[level] : BRAND.dark,
  };
}

function drawTimeAbove(pdf, above, withLevel, x, y) {
  const cell = timeAboveCell(above, withLevel);
  if (!cell) {
    drawValue(pdf, null, x, y);
    return;
  }
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...cell.color);
  pdf.text(cell.main, x, y - LINE_GAP / 2);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(DURATION_FONT);
  pdf.setTextColor(...BRAND.dark);
  pdf.text(cell.duration, x, y + LINE_GAP / 2);
  pdf.setFontSize(VALUE_FONT);
}

/** Légende sous le tableau : les deux colonnes de temps et les paliers D3. */
export function statsLegendTexts() {
  const pct = (v) => formatNumberLocale(v, 0);
  return [
    i18n.t('pdf.stats.legend.warning', { low: pct(TIME_ABOVE_PCT.watchFrom), high: pct(TIME_ABOVE_PCT.highAbove) }),
    i18n.t('pdf.stats.legend.alert', { table: i18n.t('pdf.section.thresholds') }),
  ];
}

/**
 * Dessine le tableau des statistiques PM avec couleurs de seuils
 * P95 et temps au-dessus des seuils d'avertissement et d'alerte viennent
 * toujours de l'objet de buildPeriodAnalysis, celui des cartes de l'écran
 * (D241) : la période analysée quand elle est active (D240, P95 « Non
 * affiché » sous 30 mesures), sinon la même fonction sur toutes les mesures,
 * comme SessionStats sans période. Le PDF ne recalcule rien de son côté.
 * @param {Object} [periodStats] `exposure` et `hiddenP95` de la période active.
 */
export function drawStatsTable(pdf, session, readings, yPos, margin, pageWidth, periodStats) {
  const tableWidth = pageWidth - 2 * margin;
  const { exposure, hiddenP95 } = periodStats || buildPeriodAnalysis(session, readings, null);
  const extra = (key) => ({ ...exposure[key], p95Hidden: hiddenP95.includes(key) });

  const colX = STATS_COLUMNS.map((_, i) => margin + STATS_COLUMNS.slice(0, i).reduce((a, b) => a + b, 0) * tableWidth);

  // Header
  pdf.setFillColor(...BRAND.blue1);
  pdf.rect(margin, yPos - 6, tableWidth, ROW_H, 'F');
  pdf.setTextColor(...BRAND.white);
  pdf.setFontSize(VALUE_FONT);
  pdf.setFont('helvetica', 'bold');
  [
    i18n.t('pdf.stats.header.parameter'),
    i18n.t('pdf.stats.header.min'),
    i18n.t('pdf.stats.header.avg'),
    i18n.t('pdf.stats.header.max'),
    i18n.t('pdf.stats.header.p95'),
    i18n.t('pdf.stats.header.aboveWarning'),
    i18n.t('pdf.stats.header.aboveAlert'),
    i18n.t('pdf.stats.header.rating'),
  ].forEach((h, i) => {
    pdf.text(h, colX[i] + CELL_PADDING, yPos);
  });
  yPos += ROW_H;

  // Rows PM
  const pmRows = [
    { label: 'PM1 (µg/m³)',    min: session.pm1_min,      avg: session.pm1_avg,      max: session.pm1_max,      pmKey: 'pm1',  ex: extra('pm1') },
    { label: 'PM2.5 (µg/m³)',  min: session.pm2_5_min,    avg: session.pm2_5_avg,    max: session.pm2_5_max,    pmKey: 'pm25', ex: extra('pm25') },
    { label: 'PM10 (µg/m³)',   min: session.pm10_min,     avg: session.pm10_avg,     max: session.pm10_max,     pmKey: 'pm10', ex: extra('pm10') },
    { label: `${i18n.t('pdf.stats.row.temperature')} (°C)`, min: session.temp_min,   avg: session.temp_avg,     max: session.temp_max,     isTemp: true },
    { label: `${i18n.t('pdf.stats.row.humidity')} (%)`,   min: session.humidity_min,  avg: session.humidity_avg, max: session.humidity_max, isHumidity: true },
  ];

  pmRows.forEach((row, idx) => {
    const rowH = row.ex ? PM_ROW_H : ROW_H;
    const top = yPos - 6;
    // Ligne de base des cellules d'une ligne, centrée dans la rangée.
    const y = top + rowH / 2 + 1.5;
    if (idx % 2 === 0) {
      pdf.setFillColor(248, 249, 250);
      pdf.rect(margin, top, tableWidth, rowH, 'F');
    }

    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...BRAND.dark);
    pdf.setFontSize(VALUE_FONT);
    pdf.text(row.label, colX[0] + CELL_PADDING, y);

    pdf.setFont('helvetica', 'normal');
    drawValue(pdf, row.min, colX[1] + CELL_PADDING, y);
    drawValue(pdf, row.avg, colX[2] + CELL_PADDING, y);
    drawValue(pdf, row.max, colX[3] + CELL_PADDING, y);

    // P95 et temps au-dessus des seuils (PM uniquement) ; sans objet pour
    // température et humidité : cellules laissées vides.
    if (row.ex) {
      if (row.ex.p95Hidden) drawMuted(pdf, i18n.t('pdf.stats.p95Hidden'), colX[4] + CELL_PADDING, y);
      else drawValue(pdf, row.ex.p95, colX[4] + CELL_PADDING, y);
      drawTimeAbove(pdf, row.ex.warning, true, colX[5] + CELL_PADDING, y);
      drawTimeAbove(pdf, row.ex.alert, false, colX[6] + CELL_PADDING, y);
      pdf.setFont('helvetica', 'normal');
    }

    // Évaluation
    const avg = row.avg;
    let evalText = i18n.t('common.notMeasured'), evalColor = BRAND.gray;
    if (avg != null) {
      if (row.isTemp) {
        if (avg >= 5 && avg <= 25) { evalText = i18n.t('pdf.eval.normal'); evalColor = BRAND.green; }
        else { evalText = i18n.t('pdf.eval.outOfRange'); evalColor = BRAND.orange; }
      } else if (row.isHumidity) {
        if (avg >= 40 && avg <= 70) { evalText = i18n.t('pdf.eval.normal'); evalColor = BRAND.green; }
        else { evalText = i18n.t('pdf.eval.outOfRange'); evalColor = BRAND.orange; }
      } else {
        // Classification déléguée à classifyPm (bornes <=, D169) : une valeur PILE
        // sur un seuil appartient au palier INFÉRIEUR — identique au badge écran
        // et à l'alerte e-mail backend (pmAlertService, comparaisons strictes >).
        const { level } = classifyPm(avg, row.pmKey);
        evalText = i18n.t(PM_EVAL[level].labelKey);
        evalColor = PM_EVAL[level].color;
      }
    }
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...evalColor);
    pdf.text(evalText, colX[7] + CELL_PADDING, y);

    yPos += rowH;
  });

  // Légende : ce que mesurent les deux colonnes de temps, paliers D3.
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(LEGEND_FONT);
  pdf.setTextColor(...BRAND.gray);
  let y = yPos - 6 + 4;
  statsLegendTexts().forEach((text) => {
    pdf.splitTextToSize(text, tableWidth).forEach((line) => {
      pdf.text(line, margin, y);
      y += 3.5;
    });
  });

  pdf.setTextColor(...BRAND.dark);
  return y + 5;
}

/**
 * drawSummary — Encadré "Interprétation automatique" dans le PDF
 *
 * @param {jsPDF}   pdf          - instance jsPDF
 * @param {string[]} sentences   - tableau de phrases générées par generateSummary()
 * @param {number}  yPos         - position verticale courante
 * @param {number}  margin       - marge gauche/droite
 * @param {number}  contentWidth - largeur utile
 * @param {number}  pageHeight   - hauteur de page
 * @returns {number}             - nouvelle position Y
 */
export function drawSummary(pdf, sentences, yPos, margin, contentWidth, pageHeight) {
  if (!sentences || sentences.length === 0) return yPos;

  // Estimer la hauteur nécessaire : titre (12) + note (8) + N phrases (≈ 6 chacune) + padding
  const estimatedHeight = 12 + 8 + sentences.length * 7 + 14;
  yPos = checkPageBreak(pdf, yPos, estimatedHeight, margin, pageHeight);

  // ─── Encadré bleu clair ──────────────────────────────────────────────────
  const boxTop = yPos - 5;

  // Calculer la hauteur réelle en comptant les lignes wrappées
  const lineHeight = 5.5;
  pdf.setFontSize(9);
  const allLines = sentences.flatMap(s =>
    pdf.splitTextToSize('• ' + s, contentWidth - 8)
  );
  const boxHeight = 10 + allLines.length * lineHeight + 18; // titre + lignes + note + padding

  // Fond bleu très clair
  pdf.setFillColor(240, 247, 255);
  pdf.rect(margin, boxTop, contentWidth, boxHeight, 'F');

  // Bordure gauche bleue (4mm)
  pdf.setFillColor(27, 119, 241);
  pdf.rect(margin, boxTop, 3, boxHeight, 'F');

  // Bordure extérieure légère
  pdf.setDrawColor(191, 219, 254);
  pdf.setLineWidth(0.3);
  pdf.rect(margin, boxTop, contentWidth, boxHeight, 'S');

  // ─── Titre de la section ──────────────────────────────────────────────────
  yPos = boxTop + 8;
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(27, 119, 241);
  pdf.text(i18n.t('pdf.summary.title'), margin + 7, yPos);

  // Badge "À valider par un vétérinaire"
  pdf.setFontSize(7.5);
  pdf.setFont('helvetica', 'italic');
  pdf.setTextColor(107, 114, 128);
  pdf.text(i18n.t('pdf.summary.toValidate'), margin + contentWidth - 3, yPos, { align: 'right' });

  yPos += 6;

  // ─── Phrases du résumé ────────────────────────────────────────────────────
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(44, 62, 80);

  for (const sentence of sentences) {
    const lines = pdf.splitTextToSize('• ' + sentence, contentWidth - 8);
    for (const line of lines) {
      pdf.text(line, margin + 7, yPos);
      yPos += lineHeight;
    }
  }

  yPos += 3;

  // ─── Note de bas d'encadré ───────────────────────────────────────────────
  pdf.setFontSize(7.5);
  pdf.setFont('helvetica', 'italic');
  pdf.setTextColor(107, 114, 128);
  const noteLines = pdf.splitTextToSize(
    i18n.t('pdf.summary.note'),
    contentWidth - 8
  );
  noteLines.forEach(line => {
    pdf.text(line, margin + 7, yPos);
    yPos += 4;
  });

  yPos += 6;

  pdf.setTextColor(44, 62, 80);
  pdf.setDrawColor(0);
  pdf.setLineWidth(0.2);

  return yPos;
}
